import { BCChat, findMessage, IAlertTypes } from '@bimser/components';
import { IAssistantMessage } from '@bimser/components/dist/BCChat/entities/IMessage';
import { IDictionary } from '@bimser/core';
import React from 'react';
import { IUseAiAssistantProps } from '../../entities/IAiAssistant';
import { ErrorCodes } from '../../services/ErrorCodes';
import HTTPRequest from '../../services/HTTPRequest';
import { ServiceList } from '../../services/serviceList';

export const useAiAssistant = (props: IUseAiAssistantProps) => {
	const [notifications, setNotifications] = React.useState<
		(typeof BCChat.defaultProps)['notifications']
	>([]);
	const [loading, setLoading] = React.useState<boolean>(false);

	const pushOrUpdateMessage = (firstRun: boolean, message: IAssistantMessage) => {
		if (firstRun) {
			props.pushMessage(message);
		} else {
			props.updateMessage(message);
		}
	};

	const displayErrorAlert = error => {
		setNotifications([
			{
				key: Date.now().toString(),
				type: IAlertTypes.error,
				description: findMessage.get('102106'),
				message: ErrorCodes[error?.exception?.Code] || findMessage.get('102639')
			}
		]);
		setLoading(false);
		console.error(error);
	};

	const onPromptSent = React.useCallback(
		async (prompt: string, payload?: IDictionary<any>) => {
			setLoading(true);

			props.pushMessage({
				content: prompt,
				role: 'user',
				key: Date.now().toString()
			});

			const url =
				(window as any).serviceConfig.services.web.baseURL + '/Ai/ChatWithHelpAsistant';

			try {
				const response = await fetch(url, {
					method: 'POST',
					body: JSON.stringify(
						payload || {
							sessionId: props.sessionId,
							prompt,
							refresh: false,
							source: props.payload.panelLocation,
							projectSecretKey: props.payload.projectSecretKey,
							projectPassKey: props.payload.projectPassKey
						}
					),
					headers: {
						...HTTPRequest.defaultHeaders,
						'Content-Type': 'application/json'
					}
				});

				if (!response.ok) {
					throw await response.json();
				}

				const reader = response.body.getReader();
				const decoder = new TextDecoder('utf-8');
				const assistantMessage: IAssistantMessage = {
					content: '',
					key: response.headers.get('Feedback_id'),
					role: 'assistant',
					streaming: true
				};

				const processStream = async (firstRun: boolean) => {
					try {
						const { done, value } = await reader.read();

						if (done) {
							assistantMessage.streaming = false;
							pushOrUpdateMessage(firstRun, assistantMessage);
							setLoading(false);
							props.updateSessionId(response.headers.get('session_id'));
							return;
						}

						assistantMessage.content += decoder.decode(value, { stream: true });
						pushOrUpdateMessage(firstRun, assistantMessage);
						processStream(false);
					} catch (error) {
						displayErrorAlert(error);
					}
				};

				processStream(true);
			} catch (error) {
				displayErrorAlert(error);
			}
		},
		[props.sessionId, props.payload]
	);

	const onResponseSent = React.useCallback(
		(args: Parameters<(typeof BCChat.defaultProps)['onResponseSent']>[0]) => {
			const request = {
				sessionId: props.sessionId,
				feedBack: args.type == 'good',
				feedBackDetail: args.feedback,
				feedBackId: args.message.key
			};

			return ServiceList.web.Ai.FeedBack.callPromise(request, true);
		},
		[props.sessionId]
	);

	// Oturumu Yeniden Başlat
	const onRefreshSession = () => {
		props.clearMessages();
		setNotifications([]);

		onPromptSent(findMessage.get('103718'), {
			projectSecretKey: props.payload.projectSecretKey,
			projectPassKey: props.payload.projectPassKey,
			source: props.payload.panelLocation
		});
	};

	// Önbelleği Yenile
	const onRefreshCache = () => {
		setNotifications([]);

		onPromptSent(findMessage.get('103719'), {
			sessionId: props.sessionId,
			refresh: true,
			projectSecretKey: props.payload.projectSecretKey,
			projectPassKey: props.payload.projectPassKey,
			source: props.payload.panelLocation
		});
	};

	return {
		notifications,
		loading,
		onPromptSent,
		onResponseSent,
		onRefreshSession,
		onRefreshCache
	};
};
